/**
 * PostArticle Component
 *
 * Diamond Architecture: Shared article content for blog post pages.
 * Encapsulates all localized field resolution, heading extraction, and rendering.
 *
 * Used by:
 * - /blog/[slug] (production page with TOC sidebar, related posts)
 * - /preview/posts/[id] (staff preview iframe)
 *
 * The parent page owns the layout (grid, sidebar, background).
 * This component owns the content (header, audio, body, partner card).
 */
import { Link } from '@/shared/i18n/routing';
import { extractHeadings } from '@/shared/lib/content/extractHeadings';
import { canonicalInternalUrl } from '@/shared/lib/routing/canonical-internal-path';
import { useConfig } from '@/shared/providers/ConfigProvider';
import { canonicalizeArticleLinks } from '@letsgethome/lib/blog';
import { projectLocalized, type LocalizedStringData } from '@letsgethome/lib/i18n';
import { formatDate } from '@letsgethome/lib/utils/timestamp';
import { routes } from '@letsgethome/routes';
import {
  plaintextString,
  slug,
  type ImageId,
  type Locale,
  type LocalizedText,
  type S3Key,
  type SafeHtmlString,
} from '@letsgethome/types/branded';

import { AudioPlayer } from './AudioPlayer';
import { PartnerCard } from './PartnerCard';
import { PostContent } from './PostContent';
import { TableOfContentsMobile, type TocHeading } from './TableOfContents';

// Post data shape (matches proto response). Body variants are pre-sanitized.
export interface PostData {
  title: LocalizedStringData;
  body: { en: SafeHtmlString; zhHk: SafeHtmlString; zhCn: SafeHtmlString };
  categoryName: LocalizedStringData;
  audioUrl: LocalizedStringData;
  partnerName: string;
  partnerSlug: string;
  partnerLogoKey: S3Key;
  partnerLogoHorizontalKey: S3Key;
  partnerRole: LocalizedStringData;
  publishedAt: string | null;
  images: Array<{
    id: ImageId;
    key: S3Key;
    filename: string;
    sortOrder: number;
    width?: number;
    height?: number;
  }>;
}

export interface PostArticleProps {
  post: PostData;
  locale: Locale;
  /** Translation function for i18n strings */
  translations: {
    listenToArticle: LocalizedText;
    byAuthor: (author: string) => LocalizedText;
    tableOfContents: LocalizedText;
    partnerRoleFallback: LocalizedText;
  };
  /** If true, partner card is non-interactive (for preview iframe) */
  isPreview?: boolean;
  /** If true, show mobile TOC (parent handles desktop TOC) */
  showMobileToc?: boolean;
  /** Callback to receive extracted headings (for parent's desktop TOC) */
  onHeadingsExtracted?: (headings: TocHeading[]) => void;
}

/**
 * Prepare post article data and extract headings.
 * Call this in the parent to get headings for desktop TOC.
 *
 * Deliberately returns NO body. It used to hand back `bodyWithIds`, which no
 * caller rendered — but that made a second, un-canonicalized path to body HTML
 * (see {@link PostArticle}, which is where the body is actually rendered and
 * where legacy article links are rewritten). Withholding it here means a future
 * caller cannot accidentally render the un-rewritten copy. Headings are
 * unaffected by the link rewrite, so they are safe to derive from the raw body.
 */
export function preparePostArticle(
  post: PostData,
  locale: Locale
): { headings: TocHeading[]; title: string } {
  const body = projectLocalized(post.body, locale);
  const title = projectLocalized(post.title, locale);
  const { headings } = extractHeadings(body);
  return { headings, title };
}

export function PostArticle({
  post,
  locale,
  translations,
  isPreview = false,
  showMobileToc = true,
}: PostArticleProps) {
  // Resolve all localized fields
  const title = projectLocalized(post.title, locale);
  const body = projectLocalized(post.body, locale);
  const categoryName = projectLocalized(post.categoryName, locale);
  const audioUrl = projectLocalized(post.audioUrl, locale);
  const partnerRole = projectLocalized(post.partnerRole, locale);

  // THE one place a post body becomes rendered HTML, so it is where stored
  // author copy gets its non-canonical links rewritten: the Rails-era
  // `/blog/<slug>/<id>` form that hits the legacy loader and 410s (79 URLs in
  // the 2026-07-20 crawl), and every spelling the request-side redirect
  // middleware would 301 — `/zh-HK/<page>` (zh-HK is the unprefixed default
  // locale), the retired `/free-tools/…` vocabulary, trailing slashes (three
  // traffic-earning posts sat behind that hop in the 2026-08-08 crawl). Only
  // body copy can express these; `routes.blog.post()` cannot. Passing
  // `canonicalInternalUrl` — the SAME function the middleware runs — is what
  // keeps the link side and the request side from drifting.
  const { siteUrl } = useConfig();
  // Safe cast: body inputs are pre-sanitized SafeHtmlString; neither transform
  // introduces unsafe content — extractHeadings only adds `id` attributes to
  // heading tags, and canonicalizeArticleLinks only rewrites same-origin hrefs.
  const { html: bodyWithIds, headings } = extractHeadings(
    canonicalizeArticleLinks(body, siteUrl, canonicalInternalUrl)
  );
  const safeBodyWithIds = bodyWithIds as SafeHtmlString;

  // Format date — in the viewer's locale, market timezone (lib SSOT).
  const formattedDate = isPreview
    ? formatDate(new Date(), locale)
    : formatDate(post.publishedAt, locale);

  return (
    <article className="max-w-3xl">
      {/* Mobile TOC - Shown on mobile/tablet when enabled */}
      {showMobileToc && headings.length > 0 && (
        <div className="xl:hidden mb-8">
          <TableOfContentsMobile headings={headings} title={translations.tableOfContents} />
        </div>
      )}
      {/* Header */}
      <header className="mb-8">
        {categoryName && (
          <span className="inline-block bg-primary/10 text-primary text-sm font-medium px-3 py-1 rounded-full mb-4">
            {plaintextString(categoryName)}
          </span>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold">{plaintextString(title)}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          {post.partnerName && (
            <>
              {isPreview || !post.partnerSlug ? (
                <span>{translations.byAuthor(post.partnerName)}</span>
              ) : (
                <Link
                  href={routes.partners.detail(slug(post.partnerSlug))}
                  className="hover:text-primary"
                >
                  {translations.byAuthor(post.partnerName)}
                </Link>
              )}
            </>
          )}
          <time suppressHydrationWarning={isPreview}>{formattedDate}</time>
        </div>
      </header>
      {/* Audio Player */}
      {audioUrl && (
        <AudioPlayer src={audioUrl} title={translations.listenToArticle} className="mb-8" />
      )}
      {/* Body with Images */}
      <PostContent
        body={safeBodyWithIds}
        images={post.images.map((img) => ({
          id: img.id,
          key: img.key,
          filename: img.filename,
          sortOrder: img.sortOrder,
          width: img.width || undefined,
          height: img.height || undefined,
        }))}
        altText={title}
        partnerLogoHorizontalKey={post.partnerLogoHorizontalKey || undefined}
        partnerName={post.partnerName || undefined}
      />
      {/* Partner Card */}
      {post.partnerName && (
        <PartnerCard
          partnerName={post.partnerName}
          partnerSlug={post.partnerSlug}
          partnerLogoKey={post.partnerLogoKey}
          partnerRole={plaintextString(partnerRole || '') || translations.partnerRoleFallback}
          isPreview={isPreview}
        />
      )}
    </article>
  );
}
